import { useUser } from "@/features/user/hooks/use-user-vue";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { getExpirationDate, setExpirationToken } from "@/redux/slices/userSlice";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { jwtDecode } from "../invalid-token-error";
import { getJwt } from "../utils";
import { useRefreshUser } from "./use-refresh-user";

export const useUserActivity = () => {
  const { refreshProfile, user } = useUser();
  const userLogin = useRef(user);
  const inactivityTime = 1000 * 60 * 4;
  const userLastActivity = useRef(Date.now());
  const userExpDate = useAppSelector(getExpirationDate);
  const isRefreshing = useRef<boolean>(false);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const timoutRef = useRef<NodeJS.Timeout | null>(null);
  const { logout, refreshToken } = useRefreshUser();
  const token = getJwt().token;

  useEffect(() => {
    if (location.pathname.includes("change-partner")) return;

    const handleUserActivity = () => {
      if (getJwt().token === null) return;
      if (userLogin.current) {
        userLastActivity.current = new Date().getTime();
      }
    };
    window.addEventListener("focus", handleUserActivity);
    window.addEventListener("keypress", handleUserActivity);
    window.addEventListener("blur", handleUserActivity);
    window.addEventListener("click", handleUserActivity);
    window.addEventListener("scroll", handleUserActivity);
    window.addEventListener("mousemove", handleUserActivity);
    window.addEventListener("mousewheel", handleUserActivity);
    window.addEventListener("mousedown", handleUserActivity);
    window.addEventListener("MSPointerDown", handleUserActivity);
    window.addEventListener("MSPointerMove", handleUserActivity);
    window.addEventListener("visibilitychange", handleUserActivity);

    return () => {
      window.removeEventListener("focus", handleUserActivity);
      window.removeEventListener("blur", handleUserActivity);
      window.removeEventListener("click", handleUserActivity);
      window.removeEventListener("keypress", handleUserActivity);
      window.removeEventListener("scroll", handleUserActivity);
      window.removeEventListener("mousemove", handleUserActivity);
      window.removeEventListener("mousewheel", handleUserActivity);
      window.removeEventListener("mousedown", handleUserActivity);
      window.removeEventListener("MSPointerDown", handleUserActivity);
      window.removeEventListener("MSPointerMove", handleUserActivity);
      window.removeEventListener("visibilitychange", handleUserActivity);
    };
  }, []);

  useEffect(() => {
    const refreshUserProfile = async () => {
      if (user === null && token) await refreshProfile();
    };

    refreshUserProfile();
  }, [token]);

  const triggerRefresh = async () => {
    if (isRefreshing.current) return;
    const now = new Date().getTime();
    const userActive = now - userLastActivity.current < inactivityTime;
    if (!userActive) {
      await logout();
      userLogin.current = null;
      return;
    }
    try {
      isRefreshing.current = true;
      await refreshToken();
      await refreshProfile();
    } finally {
      isRefreshing.current = false;
    }
    return;
  };
  useEffect(() => {
    if (location.pathname.includes("change-partner")) return;
    const token = getJwt()?.token ?? null;
    if (!token) {
      logout();
      return;
    }
    let expireDate = userExpDate;
    if (userExpDate === null) {
      const { exp } = jwtDecode(token);
      expireDate = exp ?? null;
      dispatch(setExpirationToken(Number(exp)));
    }
    if (expireDate !== null) {
      const jwtExpirationDate = new Date(expireDate * 1000).getTime();
      const now = new Date().getTime();
      const minuteBefore = 60_000;
      const timeLeft = jwtExpirationDate - now;
      const timoutTime = timeLeft - minuteBefore;
      if (timeLeft <= 0) {
        logout();
        return;
      }
      if (timeLeft <= minuteBefore) {
        triggerRefresh();
        return;
      }

      timoutRef.current = setTimeout(async () => {
        await triggerRefresh();
      }, timoutTime);
    }
    if (timoutRef.current) {
      return () => clearTimeout(timoutRef.current ?? undefined);
    }
  }, [userExpDate]);

  useEffect(() => {
    userLogin.current = user;
  }, [user]);

  return {
    useRefreshUser
  };
};
